// ── Bottom toast rail — single occupancy ─────────────────────────────────────
// Four independent surfaces all anchor to the bottom of the screen within 24px of
// each other, at the same z-index:
//
//   EpisodePulse peek    bottom 72px
//   UndoToast            bottom 78px
//   RatingSavedToast     bottom 82px
//   Celebration          bottom 96px
//
// Nothing coordinated them, so any two firing together simply overlapped. Marking a
// LIST of shows watched, then an individual show, then Episode Pulse produced three
// toasts piled on one another (Jason 2026-07-27) — the same class of bug usePromptSlot
// already solves for full-screen prompts, and the reason isOverlayBusy() exists for
// modals. This is the missing third case: toast-vs-toast.
//
// Deliberately a CLAIM, not a queue. These are passive confirmations of something the
// user just did; if two land at once the second is near-duplicate noise ("marked 6
// watched" then "marked 1 watched"). Showing one and dropping the other is better than
// making someone watch a train of them. Surfaces that genuinely must not be lost
// (Celebration) already queue internally and simply wait their turn for the rail.
//
// Holders MUST release — every consumer releases on hide and on unmount. A stuck holder
// silences the rail, so releasing is unconditional in cleanup rather than conditional on
// state that might already have changed.
let holder = null
const subs = new Set()

const notify = () => subs.forEach(fn => { try { fn() } catch { /* never let one break the rest */ } })

// True when SOMEONE ELSE holds the rail. Pass your own id so the holder never blocks itself
// (a re-render of the visible toast must not read as "busy" and hide it).
export function railBusy(id) {
  return !!holder && holder !== id
}

// Take the rail if it's free (or already ours). Returns whether we hold it.
// Notifies on a NEW claim, not just on release: consumers claim from an effect, i.e.
// AFTER their first paint, so two toasts firing together both render, then both claim.
// Without a notify here the loser never re-checks and stays on screen — which is exactly
// the bug this module exists to fix, and it survived my first pass (two toasts still
// overlapped in the repro). The notify makes the loser re-render, see railBusy() and
// stand down.
export function claimRail(id) {
  if (holder && holder !== id) return false
  const isNew = holder !== id
  holder = id
  if (isNew) notify()
  return true
}

export function releaseRail(id) {
  if (holder === id) { holder = null; notify() }
}

// Re-check when the rail frees up. Consumers already poll while held, but this makes the
// hand-off immediate instead of up to a poll interval late.
export function subscribeRail(fn) {
  subs.add(fn)
  return () => subs.delete(fn)
}

// Test seam — module state persists across a suite otherwise.
export function __resetRail() { holder = null; subs.clear() }
